import React, { useEffect, useState } from "react";
import { createBrowserRouter, ScrollRestoration } from "react-router-dom";
import App from "../App";
import { AliveScope } from "react-activation";

import config from "../utils/config";
import utils from "../utils";
import {
  getBrowserFingerPrintId,
  reportHomeAppActive,
  setCurrentBfid,
} from "../utils/BrowserFingerprintId";
import locale from "../utils/locale";
import { MusicContextProvider } from "../contexts/MusicContext";
import AppProvider from "../providers/AppProvider";
import { PageLoadingProvider } from "../contexts/PageLoadingContext";
import { loadConfig } from "../modules/homeDisplay/services";
import { useStartupService } from "../modules/startup/stores";
import { useThemeService } from "../modules/theme/stores";
import { useUserAuth } from "../modules/user/stores";
import { ROUTES, useNavigation } from "../utils/navigation";
import { useStartupStore } from "../modules/startup/stores";
import { useWsInit } from "../hooks/useEvent";
import { ConfigProvider as AntdConfigProvider, theme as antdTheme } from "antd";
import { ConfigProvider as AntdMobileConfigProvider } from "antd-mobile";
import enUS from "antd-mobile/es/locales/en-US";
import ptBR from "antd-mobile/es/locales/pt-BR";
import esES from "antd-mobile/es/locales/es-ES";
import { setGlobalValue } from "../stores/GlobalState";
import LoadingSpinner from "../components/LoadingSpinner";
import { useTranslation } from "react-i18next";

const antdMobileLocaleMap: Record<string, typeof enUS> = {
  en: enUS,
  pt: ptBR,
  es: esES,
};

function AntdMobileLocaleProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const antdMobileLocale = antdMobileLocaleMap[i18n.language] || enUS;
  return (
    <AntdMobileConfigProvider locale={antdMobileLocale}>
      {children}
    </AntdMobileConfigProvider>
  );
}

config.init();
setGlobalValue("shownDialogIds", []);

function AppInitializer({ children }: { children: React.ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const [startupError, setStartupError] = useState<string | null>(null);
  const [shouldReplaceUrlToHome, setShouldReplaceUrlToHome] = useState(false);

  const { loadStartupData } = useStartupService();
  const { loadTheme } = useThemeService();
  const { loadUserInfo, loadGuestInfo } = useUserAuth();
  const { navigate } = useNavigation();

  useWsInit();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // 并行发起 startup 和 homeDisplay config 请求
        const [startupResult] = await Promise.all([
          loadStartupData(true),
          loadConfig(),
        ]);

        // startup 完成后，并行加载语言包等任务
        const parallelTasks: Promise<any>[] = [];

        // 加载语言包
        if (startupResult?.langItemsCacheKey) {
          const loadLocale = async () => {
            const resp = await utils.request(
              "/static/lang/" + startupResult?.langItemsCacheKey,
              null,
              { method: "GET" }
            );
            if (resp && resp.data && resp.data.model) {
              locale.register(resp.data.model, () => {});
            }
          };
          parallelTasks.push(loadLocale());
        }

        // 邀请码逻辑 - 支持 ?p=xxx 或短链 /c-xxx（URL 里 p 不带 c- 前缀，如 p=R5Gwg1xj）
        const searchParams = new URLSearchParams(window.location.search);
        const pathMatch = window.location.pathname.match(/^\/c-(.+)$/);
        const inviteCode =
          searchParams.get("p") || (pathMatch ? pathMatch[1].trim() : null);
        const redpacketCode = searchParams.get("r"); // 红包码

        // 投放渠道 ch：优先 URL，其次缓存；有 ch 则写入/同步缓存并拉取 launch_agent 邀请人供注册页使用
        const chFromUrl = searchParams.get("ch") || utils.getQueryString("ch");
        const chFromCache = utils.getObject("ch") || window.__ch;
        const chToUse =
          (chFromUrl && chFromUrl.trim()) ||
          (chFromCache && String(chFromCache).trim()) ||
          "";
        if (chFromUrl && chFromUrl.trim()) {
          utils.setObject("ch", chFromUrl.trim());
          window.__ch = chFromUrl.trim();
        }
        if (chToUse) {
          parallelTasks.push(
            utils.fetchLaunchAgentAndCache(chToUse).catch(() => {})
          );
        }
        const token = utils.getObject("token");
        const userInfo = utils.getUser();
        const isLoggedIn =
          (token && token !== "null" && token.length >= 10) ||
          (userInfo && typeof userInfo === "object" && userInfo !== null);

        // 红包码逻辑 - 如果检测到 r 参数，保存到 sessionStorage，GlobalRedpacket 组件会处理显示
        if (redpacketCode) {
          sessionStorage.setItem("redpacketCode", redpacketCode);
        }

        if (!isLoggedIn && inviteCode) {
          utils.setObject("invitedCode", inviteCode);
          sessionStorage.setItem("pendingRegisterInviteCode", inviteCode);
          if (pathMatch) {
            setShouldReplaceUrlToHome(true);
          }
        }

        await Promise.allSettled(parallelTasks);
        setInitialized(true);
      } catch (error) {
        console.error("应用初始化失败:", error);
        setStartupError(error instanceof Error ? error.message : "初始化失败");
        setInitialized(true);
      }
    };

    initializeApp();
  }, [loadStartupData, loadTheme, loadUserInfo, loadGuestInfo]);

  useEffect(() => {
    if (initialized && shouldReplaceUrlToHome) {
      setShouldReplaceUrlToHome(false);
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [initialized, shouldReplaceUrlToHome, navigate]);

  if (startupError) {
    console.warn("启动过程中出现错误:", startupError);
  }
  if (!initialized) {
    return <LoadingSpinner />;
  }
  return <>{children}</>;
}

function Entry() {
  const browserFingerprintId = utils.getObject("BrowserFingerprintId");
  const [inited, setInited] = useState(!!browserFingerprintId);
  const [checked, setChecked] = useState(false);
  const appType = useStartupStore(s => s.startupData?.appType);

  useEffect(() => {
    const init = async () => {
      const hasInited = inited;
      const currentFingerprintId =
        utils.getObject("BrowserFingerprintId") ||
        (await getBrowserFingerPrintId());

      reportHomeAppActive(currentFingerprintId, appType);
      setCurrentBfid(currentFingerprintId);

      if (hasInited) return;
      if (window._cburl) {
        window.location.href = window._cburl;
        return;
      }
      setInited(true);
    };
    init().catch((err: unknown) => {
      console.error("首页指纹初始化失败:", err);
    });
  }, []);

  useEffect(() => {
    if (!checked) setChecked(true);
  }, []);

  if (inited && checked) {
    utils.hideInitialLoading();
    return <App />;
  }
  return null;
}

export const router = createBrowserRouter([
  {
    path: "/*",
    element: (
      <AntdConfigProvider
        theme={{
          algorithm: antdTheme.defaultAlgorithm,
        }}
        prefixCls="antd"
      >
        <AntdMobileLocaleProvider>
          <PageLoadingProvider>
            <AppInitializer>
              <MusicContextProvider>
                <AppProvider>
                  <AliveScope>
                    <Entry />
                  </AliveScope>
                </AppProvider>
              </MusicContextProvider>
            </AppInitializer>
            <ScrollRestoration
              getKey={location => {
                if (
                  location.pathname.includes(ROUTES.GAME_DETAIL) ||
                  location.pathname === ROUTES.HOME ||
                  location.pathname === ROUTES.HOME_ALT
                ) {
                  return null;
                }
                return location.pathname;
              }}
            />
          </PageLoadingProvider>
        </AntdMobileLocaleProvider>
      </AntdConfigProvider>
    ),
  },
]);
